import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { superAdminApi, centsToAUD, audToCents } from "../../lib/events";
import { extractError } from "../../lib/api";

// mode: "ticket" | "booking"
export default function DeleteDialog({ open, onClose, mode, id, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundChoice, setRefundChoice] = useState("none");
  const [partialAud, setPartialAud] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [confirmCheckedIn, setConfirmCheckedIn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    setPreview(null); setLoading(true);
    setRefundChoice("none"); setPartialAud(""); setReason("");
    setConfirmCheckedIn(false);
    const fetcher = mode === "ticket"
      ? superAdminApi.ticketDeletePreview(id)
      : superAdminApi.orderDeletePreview(id);
    fetcher
      .then((p) => {
        setPreview(p);
        const rm = mode === "ticket" ? p?.order?.record_mode : p?.order?.record_mode;
        setNotify(rm !== "INTERNAL_TEST");
        const sug = p?.payment?.suggested_refund_cents || 0;
        setPartialAud(sug ? (sug / 100).toFixed(2) : "");
      })
      .catch((e) => { toast.error(extractError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [open, id, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = preview?.payment || {};
  const remaining = pay.remaining_refundable_cents || 0;
  const canRefund = remaining > 0;
  const isCheckedIn = mode === "ticket"
    ? preview?.ticket?.status === "checked_in"
    : (preview?.checked_in_count || 0) > 0;

  const submit = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    if (isCheckedIn && !confirmCheckedIn) return toast.error("Confirm the checked-in warning first.");
    let refundAmount;
    if (refundChoice === "partial") {
      refundAmount = audToCents(partialAud);
      if (refundAmount <= 0) return toast.error("Enter a partial refund amount.");
      if (refundAmount > remaining) return toast.error(`Max refundable is ${centsToAUD(remaining)}.`);
    }
    setBusy(true);
    try {
      const payload = {
        idempotency_key: (crypto.randomUUID && crypto.randomUUID()) || `del-${Date.now()}-${Math.random()}`,
        reason: reason.trim(),
        refund_choice: refundChoice,
        refund_amount_cents: refundChoice === "partial" ? refundAmount : undefined,
        notify_buyer: notify,
        confirm_checked_in: confirmCheckedIn || !isCheckedIn,
      };
      const res = mode === "ticket"
        ? await superAdminApi.deleteTicket(id, payload)
        : await superAdminApi.deleteBooking(id, payload);
      const rs = res.refund_status;
      let msg = mode === "ticket" ? "Ticket deleted." : `Booking deleted (${res.tickets_voided} ticket(s)).`;
      if (rs && rs !== "NOT_REQUESTED") {
        msg += rs === "SUCCEEDED" ? ` Refund ${centsToAUD(res.refund_amount_cents)} completed.`
          : rs === "PENDING" ? ` Refund ${centsToAUD(res.refund_amount_cents)} initiated.`
          : ` Refund ${rs} — needs attention.`;
      }
      (rs === "FAILED" || rs === "REQUIRES_ACTION") ? toast.warning(msg) : toast.success(msg);
      onDone && onDone(res);
      onClose();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "ticket" ? "Delete ticket" : "Delete whole booking";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sa-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight flex items-center gap-2 text-[var(--gt-accent)]">
            <Trash2 size={20} /> {title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : !preview ? null : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="border-2 border-[var(--gt-ink)] p-3 text-sm space-y-1" data-testid="sa-delete-summary">
              <Row k="Event" v={preview.event?.title} />
              {mode === "ticket" ? (
                <>
                  <Row k="Attendee" v={preview.ticket?.attendee_name} />
                  <Row k="Ticket type" v={preview.ticket?.ticket_type_name} />
                  <Row k="Code" v={preview.ticket?.code} mono />
                  <Row k="Check-in" v={preview.ticket?.status === "checked_in" ? "Checked in" : "Not checked in"} />
                </>
              ) : (
                <>
                  <Row k="Buyer" v={preview.order?.buyer_name} />
                  <Row k="Booking" v={(preview.order?.id || "").slice(0, 8)} mono />
                  <Row k="Active tickets" v={`${preview.tickets_active} / ${preview.tickets_total}`} />
                </>
              )}
              <Row k="Paid" v={pay.is_paid ? centsToAUD(pay.original_amount_cents) : "Free"} />
              {pay.previously_refunded_cents > 0 && <Row k="Already refunded" v={centsToAUD(pay.previously_refunded_cents)} />}
              <Row k="Refundable now" v={centsToAUD(remaining)} />
            </div>

            {/* Warnings */}
            {(preview.warnings || []).map((w, i) => (
              <div key={i} className="flex items-start gap-2 border-2 border-[var(--gt-accent)] bg-red-50 p-3 text-xs" data-testid={`sa-delete-warning-${i}`}>
                <AlertTriangle size={16} className="text-[var(--gt-accent)] shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}

            {/* Refund options */}
            {pay.is_paid && (
              <div className="border border-[var(--gt-line)] p-3 space-y-2" data-testid="sa-delete-refund-options">
                <div className="gt-label">Refund</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="refund" checked={refundChoice === "none"} onChange={() => setRefundChoice("none")} data-testid="sa-refund-none" />
                  No refund
                </label>
                <label className={`flex items-center gap-2 text-sm ${!canRefund ? "opacity-40" : ""}`}>
                  <input type="radio" name="refund" disabled={!canRefund} checked={refundChoice === "full"} onChange={() => setRefundChoice("full")} data-testid="sa-refund-full" />
                  Full remaining refund ({centsToAUD(remaining)})
                </label>
                <label className={`flex items-center gap-2 text-sm ${!canRefund ? "opacity-40" : ""}`}>
                  <input type="radio" name="refund" disabled={!canRefund} checked={refundChoice === "partial"} onChange={() => setRefundChoice("partial")} data-testid="sa-refund-partial" />
                  Partial
                  {refundChoice === "partial" && (
                    <input className="gt-input py-1 w-28 ml-2" value={partialAud} onChange={(e) => setPartialAud(e.target.value)}
                      placeholder="A$" data-testid="sa-refund-partial-amount" />
                  )}
                </label>
                {!pay.refundable_via_stripe && pay.is_paid && (
                  <div className="text-[11px] text-[var(--gt-ink-3)]">Off-platform payment — recorded as a manual refund (no Stripe).</div>
                )}
              </div>
            )}

            {/* Reason + notify + confirm */}
            <textarea className="gt-input w-full" rows={2} placeholder="Reason (required) *" value={reason}
              onChange={(e) => setReason(e.target.value)} data-testid="sa-delete-reason" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="sa-delete-notify" />
              Notify buyer by email
            </label>
            {isCheckedIn && (
              <label className="flex items-start gap-2 text-sm border-2 border-[var(--gt-accent)] bg-red-50 p-3" data-testid="sa-delete-confirm-checkedin">
                <input type="checkbox" checked={confirmCheckedIn} onChange={(e) => setConfirmCheckedIn(e.target.checked)} />
                <span>I understand this ticket was already checked in. The scan record will be preserved.</span>
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          <button className="gt-btn-ghost" onClick={onClose} disabled={busy} data-testid="sa-delete-cancel">Cancel</button>
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[var(--gt-accent)] text-white font-bold border-2 border-[var(--gt-ink)] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={submit} disabled={busy || loading || !reason.trim() || (isCheckedIn && !confirmCheckedIn)} data-testid="sa-delete-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Confirm delete
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--gt-ink-3)]">{k}</span>
      <span className={`font-semibold text-right ${mono ? "font-mono" : ""}`}>{v ?? "—"}</span>
    </div>
  );
}
